import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Lightbulb, CheckCircle, Heart } from 'lucide-react';
import { useValues } from '../hooks/useAPI';

const ValuesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { data: values, isLoading } = useValues();

  // Default values if API fails
  const defaultValues = [
    {
      id: 1,
      title: 'Innovation',
      description: 'We are driven by curiosity and a relentless pursuit of what\'s next, turning bold ideas into impactful solutions.',
      icon: 'lightbulb'
    },
    {
      id: 2,
      title: 'Integrity',
      description: 'We build trust through transparency, accountability, and unwavering ethical standards in everything we do.',
      icon: 'check-circle'
    },
    {
      id: 3,
      title: 'Community Empowerment',
      description: 'We are committed to nurturing local talent and driving sustainable growth in Sierra Leone and beyond.',
      icon: 'heart'
    }
  ];

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'lightbulb':
        return Lightbulb;
      case 'check-circle':
        return CheckCircle;
      case 'heart':
        return Heart;
      default:
        return Lightbulb;
    }
  };

  const displayValues = values && values.length > 0 ? values : defaultValues;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  if (isLoading) {
    return (
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-6 text-center">
          <div className="inline-block">
            <div className="spinner w-8 h-8"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="values" className="py-20 md:py-24 bg-white">
      <div className="container mx-auto px-6 text-center" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.span 
            variants={itemVariants}
            className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
          >
            OUR GUIDING PRINCIPLES
          </motion.span>
          
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-4"
          >
            Our Mission & Core Values
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl mx-auto text-gray-600 mb-12"
          >
            Our values are the foundation of our culture and the blueprint for our actions.
          </motion.p>

          <div className="grid md:grid-cols-3 gap-8 text-center">
            {displayValues.map((value, index) => {
              const Icon = getIcon(value.icon);
              return (
                <motion.div
                  key={value.id || index}
                  variants={cardVariants}
                  custom={index}
                  className="p-6 group hover:bg-gray-50 rounded-lg transition-all duration-300"
                >
                  <div className="flex justify-center mb-4">
                    <Icon className="w-12 h-12 text-lion-blue group-hover:text-lion-yellow transition-colors duration-300" />
                  </div>
                  
                  <h3 className="text-xl font-poppins font-bold text-lion-blue mb-4 group-hover:text-lion-yellow transition-colors duration-300">
                    {value.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ValuesSection;